import React, { FC, useEffect } from 'react';
import { usePusher } from '../../../contexts/PusherContext';
import { Agent, AgentEvents, ManagerNameUpdatedEvent } from '@dreamai/types';
import { formatAgentLabel } from './threadUtils';
import { useAgent } from '../../../contexts/AgentContext';

interface AgentOption {
  value: string;
  label: string;
  dreamieImageUrl: string | undefined;
  predominantColour: string | undefined;
}

interface ManagerNameListenerProps {
  agents?: Agent[];
  setAgents?: React.Dispatch<React.SetStateAction<Agent[]>>;
  selectedAgent?: AgentOption | null;
  setSelectedAgent?: React.Dispatch<React.SetStateAction<AgentOption | null>>;
}

/**
 * Component that listens for manager name update events and updates the agent list accordingly
 * Can be used with or without props:
 * - With props: Updates local state (for ChatSidebar) AND AgentContext
 * - Without props: Only updates AgentContext (for standalone use in AgentTabs)
 * This is a "headless" component that doesn't render any UI elements
 */
const ManagerNameListener: FC<ManagerNameListenerProps> = ({
  agents,
  setAgents,
  selectedAgent,
  setSelectedAgent,
}) => {
  const pusherContext = usePusher(true); // Get full context to access both channels
  const userChannel = pusherContext?.userChannel;
  const companyChannel = pusherContext?.pusherChannel;
  const { setAgents: setContextAgents } = useAgent();

  // Listen for manager name update events from Pusher
  // Listen on BOTH user channel (for user-owned agents) and company channel (for company-owned agents)
  useEffect(() => {
    if (!userChannel || !companyChannel) return;

    // Handler for manager name update events
    const handleManagerNameUpdated = (data: ManagerNameUpdatedEvent) => {
      console.log('Received manager name update event:', data);

      // Update the agent in the agents list (for ChatSidebar) - only if props provided
      if (setAgents) {
        setAgents((prevAgents) => {
          return prevAgents.map((agent) => {
            // If this agent matches the updated agent type
            if (agent.agent_type === data.agentType) {
              // Create a new agent object with the updated name and nickname
              const updatedAgent = {
                ...agent,
                name: data.newName,
                ...(data.nickname !== undefined && { nickname: data.nickname }),
              };

              // If this is the currently selected agent, update the selection too
              if (
                selectedAgent &&
                agent.id === selectedAgent.value &&
                setSelectedAgent
              ) {
                setSelectedAgent({
                  ...selectedAgent,
                  label: formatAgentLabel(updatedAgent),
                });
              }

              return updatedAgent;
            }
            return agent;
          });
        });
      }

      // Always update the AgentContext (for AgentTabs and other components)
      setContextAgents((prevAgents) => {
        return prevAgents.map((agent) => {
          if (agent.agent_type === data.agentType) {
            return {
              ...agent,
              name: data.newName,
              ...(data.nickname !== undefined && { nickname: data.nickname }),
            };
          }
          return agent;
        });
      });
    };

    // Bind to both channels
    userChannel.bind(
      AgentEvents.MANAGER_NAME_UPDATED,
      handleManagerNameUpdated,
    );
    companyChannel.bind(
      AgentEvents.MANAGER_NAME_UPDATED,
      handleManagerNameUpdated,
    );

    // Cleanup function to unbind the event listeners when component unmounts
    return () => {
      userChannel.unbind(
        AgentEvents.MANAGER_NAME_UPDATED,
        handleManagerNameUpdated,
      );
      companyChannel.unbind(
        AgentEvents.MANAGER_NAME_UPDATED,
        handleManagerNameUpdated,
      );
    };
  }, [
    userChannel,
    companyChannel,
    selectedAgent,
    setAgents,
    setSelectedAgent,
    setContextAgents,
  ]);

  // This component doesn't render anything
  return null;
};

export default ManagerNameListener;
